
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { LainaCta } from "@/components/common/LainaCta";

interface LoanExamplesSectionProps {
  language?: string;
}

const LoanExamples = [
  {
    amount: 2000,
    monthlyPayment: 71,
    term: 36,
    interestRate: 14.9
  },
  {
    amount: 5000,
    monthlyPayment: 127,
    term: 48,
    interestRate: 11.9
  },
  {
    amount: 10000,
    monthlyPayment: 219,
    term: 60,
    interestRate: 9.9
  },
  {
    amount: 50000,
    monthlyPayment: 687,
    term: 120,
    interestRate: 7.9
  }
];

const LoanExamplesSection: React.FC<LoanExamplesSectionProps> = ({ language = "fi" }) => {
  // Localized text based on language
  const localizedTexts = {
    fi: {
      title: "Esimerkkilaskelmia eri lainasummille",
      loanAmount: "Lainasumma",
      monthlyPayment: "Kuukausierä",
      loanTerm: "Laina-aika",
      interestRate: "Korko",
      ctaText: "Kilpailuta lainasi nyt",
      disclaimer: "Esimerkki: 10 000 € laina 60 kk laina-ajalla. Korko 9,9 %. Kuukausierä 219 €/kk. Todellinen vuosikorko 10,36 %. Luoton kokonaiskustannukset: 13 140 €."
    },
    he: {
      title: "דוגמאות חישוב להלוואות בסכומים שונים",
      loanAmount: "סכום הלוואה",
      monthlyPayment: "תשלום חודשי",
      loanTerm: "תקופת הלוואה",
      interestRate: "ריבית",
      ctaText: "תחרות את ההלוואה שלך כעת",
      disclaimer: "דוגמה: הלוואה בסך 10,000 € לתקופה של 60 חודשים. ריבית 9.9%. תשלום חודשי: 219 €. ריבית שנתית אפקטיבית: 10.36%. עלות כוללת: 13,140 €."
    },
    en: {
      title: "Example calculations for different loan amounts",
      loanAmount: "Loan amount",
      monthlyPayment: "Monthly payment",
      loanTerm: "Loan term",
      interestRate: "Interest rate",
      ctaText: "Compare loan offers now",
      disclaimer: "Example: €10,000 loan with 60-month term. Interest rate 9.9%. Monthly payment €219. Annual percentage rate 10.36%. Total cost of credit: €13,140."
    }
  };

  // Use the selected language or fallback to Finnish
  const text = localizedTexts[language] || localizedTexts.fi;

  return (
    <section className="py-10 md:py-12 bg-blue-50/60">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 md:mb-10" dir={language === "he" ? "rtl" : "ltr"}>
          {text.title}
        </h2>
        
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-10 ${language === "he" ? "rtl-grid" : ""}`}>
          {LoanExamples.map((example, index) => (
            <Card 
              key={index} 
              className="overflow-hidden shadow-md hover:shadow-lg transition-shadow"
            >
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-3xl font-bold text-blue-600">
                    {example.amount.toLocaleString()} €
                  </p>
                  <p className="text-sm text-gray-500">{text.loanAmount}</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                    <span className="text-gray-600">{text.monthlyPayment}</span>
                    <span className="font-semibold">{example.monthlyPayment} €/kk</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                    <span className="text-gray-600">{text.loanTerm}</span>
                    <span className="font-semibold">{example.term} kk</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{text.interestRate}</span>
                    <span className="font-semibold">{example.interestRate.toFixed(1)} %</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className={`text-center ${language === "he" ? "rtl-text" : ""}`}>
          <LainaCta 
            to={language === "he" ? "/hebrew/apply" : "/kilpailuta"}
            text={text.ctaText}
            variant="primary" 
            size="lg" 
            className="mx-auto inline-block max-w-md"
            dataTestId="loan-examples-cta"
          />
          <p className="mt-4 text-xs text-gray-500 max-w-2xl mx-auto">
            {text.disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
};

export default LoanExamplesSection;
